import React, { useEffect, useState } from 'react';
import { programsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, TrendingUp, DollarSign, Users, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';

export default function ProgramAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await programsAPI.analytics(); setAnalytics(res.data); }
      catch { toast.error('Failed to load analytics'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;
  if (!analytics) return null;

  const s = analytics.summary;

  return (
    <div className="space-y-6" data-testid="program-analytics">
      <div>
        <h1 className="font-heading text-xl font-bold tracking-tight">Program Analytics</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Sale vs cost analysis across programs and customers</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-blue-600" /><div><p className="text-xl font-bold font-mono">Rs {(s.total_sale || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Sale</p></div></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-amber-600" /><div><p className="text-xl font-bold font-mono">Rs {(s.total_cost || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Cost</p></div></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2"><TrendingUp className={`w-5 h-5 ${(s.overall_margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} /><div><p className={`text-xl font-bold font-mono ${(s.overall_margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Rs {(s.overall_margin || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Overall Margin</p></div></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-indigo-600" /><div><p className="text-xl font-bold font-mono">{s.overall_ratio ?? '-'}</p><p className="text-xs text-muted-foreground">Sale/Cost Ratio</p></div></div>
        </CardContent></Card>
      </div>

      {/* By Customer Table */}
      {analytics.by_customer?.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Analysis by Customer</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-3">Customer</th>
                    <th className="text-center py-2 px-3">Programs</th>
                    <th className="text-right py-2 px-3">Total Sale</th>
                    <th className="text-right py-2 px-3">Total Cost</th>
                    <th className="text-right py-2 px-3">Margin</th>
                    <th className="text-right py-2 px-3">Sale/Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.by_customer.map(c => (
                    <tr key={c.customer_id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 px-3 font-medium">{c.customer_name}</td>
                      <td className="py-2 px-3 text-center">{c.program_count}</td>
                      <td className="py-2 px-3 text-right font-mono">Rs {c.total_sale.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono">Rs {c.total_cost.toLocaleString()}</td>
                      <td className={`py-2 px-3 text-right font-mono font-medium ${c.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Rs {c.margin.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono">{c.sale_to_cost_ratio ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Program Table */}
      {analytics.by_program?.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><FolderKanban className="w-4 h-4" /> Analysis by Program</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-3">Program</th>
                    <th className="text-left py-2 px-3">Customer</th>
                    <th className="text-center py-2 px-3">Status</th>
                    <th className="text-center py-2 px-3">POs</th>
                    <th className="text-right py-2 px-3">Sale</th>
                    <th className="text-right py-2 px-3">Cost</th>
                    <th className="text-right py-2 px-3">Margin</th>
                    <th className="text-right py-2 px-3">Ratio</th>
                    <th className="text-center py-2 px-3">Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.by_program.map(p => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 px-3">
                        <p className="font-medium">{p.program_name}</p>
                        <p className="font-mono text-muted-foreground">{p.program_number}</p>
                      </td>
                      <td className="py-2 px-3">{p.customer_name}</td>
                      <td className="py-2 px-3 text-center">
                        <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
                      </td>
                      <td className="py-2 px-3 text-center">{p.po_count}</td>
                      <td className="py-2 px-3 text-right font-mono">Rs {p.total_sale.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono">Rs {p.total_cost.toLocaleString()}</td>
                      <td className={`py-2 px-3 text-right font-mono font-medium ${p.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Rs {p.margin.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono">{p.sale_to_cost_ratio ?? '-'}</td>
                      <td className="py-2 px-3 text-center">{p.tasks_completed}/{p.task_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
